import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TESLA_SESSION_KEY = "eviq.tesla_session_id";
const TESLA_SELECTED_VEHICLE_KEY = "eviq.tesla_selected_vehicle_id";

/**
 * useTeslaLive
 * -------------------------------------------------------------------
 * Read-side hook for the Tesla Fleet API proxy endpoints:
 *   GET /api/tesla/vehicles           → list of user's vehicles
 *   GET /api/tesla/vehicle_data       → live pack + location + climate
 *
 * Returns the currently selected vehicle's live data plus a refresh()
 * that force-bypasses the 60-second Mongo cache.
 *
 * Q1=a  → callers can consume `data` freely; when `null`, callers should
 *         fall back to demo data.
 * Q2=b  → on-demand only. No polling. Callers use refresh() from the UI.
 */
export function useTeslaLive() {
  const [sessionId, setSessionId] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      return localStorage.getItem(TESLA_SESSION_KEY);
    } catch (_e) {
      return null;
    }
  });
  const [vehicles, setVehicles] = useState([]);
  const [selectedId, setSelectedId] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      return localStorage.getItem(TESLA_SELECTED_VEHICLE_KEY);
    } catch (_e) {
      return null;
    }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Track session_id changes across other tabs / disconnect events AND
  // within the same tab (native "storage" only fires cross-tab). The Tesla
  // sign-in card dispatches "eviq:tesla-session-changed" after a fresh
  // OAuth callback so we pick up the new session immediately.
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === TESLA_SESSION_KEY) setSessionId(e.newValue);
    };
    const handleSameTab = (e) => {
      const next = e?.detail?.sessionId ?? null;
      setSessionId(next);
    };
    window.addEventListener("storage", handleStorage);
    window.addEventListener("eviq:tesla-session-changed", handleSameTab);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("eviq:tesla-session-changed", handleSameTab);
    };
  }, []);

  // Persist selection
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (selectedId) localStorage.setItem(TESLA_SELECTED_VEHICLE_KEY, selectedId);
    } catch (_e) {}
  }, [selectedId]);

  // Fetch vehicle list on connect — with a small retry so a transient
  // Tesla API blip doesn't leave us stuck on the mock catalog. Also
  // exposes whether the list has been loaded successfully so callers can
  // distinguish "waiting" from "empty account".
  const [vehiclesLoaded, setVehiclesLoaded] = useState(false);
  const [vehiclesError, setVehiclesError] = useState(null);
  const [vehiclesReason, setVehiclesReason] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setVehicles([]);
      setData(null);
      setVehiclesLoaded(false);
      setVehiclesError(null);
      setVehiclesReason(null);
      return;
    }
    let cancelled = false;
    setVehiclesError(null);

    const fetchOnce = async () => {
      const r = await fetch(
        `${API}/api/tesla/vehicles?session_id=${encodeURIComponent(sessionId)}`,
      );
      if (!r.ok) throw new Error(`vehicles ${r.status}`);
      return r.json();
    };

    (async () => {
      let list = [];
      let lastErr = null;
      // Retry up to 3 times with backoff — Fleet API occasionally 502s.
      for (let attempt = 0; attempt < 3 && !cancelled; attempt++) {
        try {
          const body = await fetchOnce();
          list = body.vehicles || [];
          if (!cancelled) setVehiclesReason(body.reason || null);
          if (list.length > 0) break;
          // Empty list is a legitimate response — no retry.
          break;
        } catch (e) {
          lastErr = e;
          await new Promise((res) => setTimeout(res, 500 * (attempt + 1)));
        }
      }
      if (cancelled) return;
      setVehicles(list);
      setVehiclesLoaded(true);
      if (list.length === 0 && lastErr) {
        setVehiclesError(String(lastErr));
      }
      if (list.length === 1) {
        setSelectedId(list[0].id);
      } else if (list.length > 1) {
        setSelectedId((cur) =>
          cur && list.some((v) => v.id === cur) ? cur : list[0].id,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Fetch vehicle_data whenever we know which vehicle is selected
  const fetchData = useCallback(
    async ({ force = false } = {}) => {
      if (!sessionId || !selectedId) return;
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(
          `${API}/api/tesla/vehicle_data?session_id=${encodeURIComponent(sessionId)}&vehicle_id=${encodeURIComponent(selectedId)}${force ? "&force=1" : ""}`,
          { cache: "no-store" },
        );
        if (r.status === 408) {
          // Vehicle asleep — surface a friendly error instead of throwing
          setError("Vehicle is asleep — tap Refresh to wake and try again");
          setData(null);
          return;
        }
        if (!r.ok) throw new Error(`vehicle_data ${r.status}`);
        const body = await r.json();
        setData(body);
        setLastUpdated(Date.now());
        // Fire a "Ready" toast the first time live data arrives per
        // session — the satisfying "you're fully connected" ping.
        try {
          const readyKey = `eviq._live_ready:${sessionId}:${selectedId}`;
          if (sessionStorage.getItem(readyKey) !== "1") {
            sessionStorage.setItem(readyKey, "1");
            const name = body?.vehicle?.display_name;
            const brand = body?.vehicle?.brand === "ford" ? "Ford"
              : body?.vehicle?.brand === "volvo" ? "Volvo" : "Tesla";
            toast.success(`Live from ${brand} · ready`, {
              description: name
                ? `${name} · battery + location streaming.`
                : "Battery + location streaming.",
            });
          }
        } catch (_e) {}
        return body;
      } catch (e) {
        setError(String(e));
      } finally {
        setLoading(false);
      }
    },
    [sessionId, selectedId],
  );

  useEffect(() => {
    if (sessionId && selectedId) fetchData({ force: false });
  }, [sessionId, selectedId, fetchData]);

  // Wake the vehicle so /vehicle_data can return live GPS + battery
  // right away instead of failing with 408 when the car is asleep.
  // Called manually via the header refresh button AND automatically
  // once per session right after OAuth completes.
  const wakeUp = useCallback(async () => {
    if (!sessionId || !selectedId) return null;
    try {
      const r = await fetch(
        `${API}/api/tesla/wake_up?session_id=${encodeURIComponent(sessionId)}&vehicle_id=${encodeURIComponent(selectedId)}`,
        { method: "POST" },
      );
      if (!r.ok) return null;
      return await r.json();
    } catch (_e) {
      return null;
    }
  }, [sessionId, selectedId]);

  // Auto wake-up + poll: once per session, when we first have a Tesla
  // session AND a selected vehicle, kick a wake_up and then re-poll
  // vehicle_data a few times (10s / 15s / 20s) until the car reports
  // battery + GPS. Guarded by sessionStorage so we don't spam Tesla
  // every time the app mounts. `wakingUp` is exposed so the header can
  // pulse a "still trying…" cue on the refresh button.
  const [wakingUp, setWakingUp] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  useEffect(() => {
    if (!sessionId || !selectedId) return;
    const guardKey = `eviq._wakeup_done:${sessionId}:${selectedId}`;
    try {
      if (sessionStorage.getItem(guardKey) === "1") return;
      sessionStorage.setItem(guardKey, "1");
    } catch (_e) {}
    let cancelled = false;
    setWakingUp(true);
    (async () => {
      await wakeUp();
      // Aggressive polling — Tesla wake_up typically transitions the car
      // to `online` within 8-15 seconds. We poll faster than that so live
      // data lands as soon as it's available. Total window: ~22 seconds.
      for (const delay of [2000, 3000, 5000, 6000, 6000]) {
        if (cancelled) return;
        await new Promise((res) => setTimeout(res, delay));
        if (cancelled) return;
        try {
          const body = await fetchData({ force: true });
          // Stop early the instant we have both battery + GPS — no need
          // to hammer Tesla after the payload is complete.
          if (body?.battery?.level != null && body?.location?.lat != null) {
            break;
          }
        } catch (_e) {}
      }
    })().finally(() => {
      if (!cancelled) setWakingUp(false);
    });
    return () => {
      cancelled = true;
      setWakingUp(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, selectedId]);

  // Manual refresh (header button): always fetch FRESH — force-bypass the
  // 60s cache, and if the car is asleep (408), wake it and keep retrying
  // until live data lands instead of silently giving up.
  const hardRefresh = useCallback(async () => {
    setWakingUp(true);
    try {
      const first = await fetchData({ force: true });
      if (first?.battery?.level != null) return first;
      await wakeUp();
      for (const delay of [2000, 3000, 5000, 6000]) {
        await new Promise((res) => setTimeout(res, delay));
        const body = await fetchData({ force: true });
        if (body?.battery?.level != null) return body;
      }
      return null;
    } finally {
      setWakingUp(false);
    }
  }, [fetchData, wakeUp]);

  const disconnect = useCallback(() => {
    try {
      localStorage.removeItem(TESLA_SESSION_KEY);
      localStorage.removeItem(TESLA_SELECTED_VEHICLE_KEY);
    } catch (_e) {}
    setSessionId(null);
    setSelectedId(null);
    setVehicles([]);
    setData(null);
  }, []);

  return {
    connected: !!sessionId,
    sessionId,
    vehicles,
    vehiclesLoaded,
    vehiclesError,
    vehiclesReason,
    selectedId,
    setSelectedId,
    data,
    loading,
    wakingUp,
    lastUpdated,
    error,
    refresh: hardRefresh,
    wakeUp,
    disconnect,
  };
}
